import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ExternalLink, Loader2, RefreshCw, Send, Trash2 } from "lucide-react";
import { AdminPageShell } from "@/components/dashboard/AdminPageShell";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import {
 listGscSites,
 getGscOverview,
 getGscTopQueries,
 getGscTopPages,
 submitGscSitemap,
 getGscSitemapStatus,
 clearGscCache,
 listChildSitemaps,
 submitGscSitemapsBatch,
 getGscSitemapStatusBatch,
 listSubmittedGscSitemaps,
 deleteGscSitemapsBatch,
 getSitemapComposition,
 type GscSitemapStatus,
} from "@/lib/gsc.functions";

export const Route = createFileRoute("/dashboard/admin/seo-search-console")({
 head: () => ({
 meta: [
 { title: "Google Search Console · Heallexa Admin" },
 { name: "robots", content: "noindex, nofollow" },
 ],
 }),
 component: GscPage,
});

const DAY_OPTIONS = [7, 28, 90];
const DEFAULT_SITE = "https://heallexa.com/";

type Overview = Awaited<ReturnType<typeof getGscOverview>>;
type RowsResp = { rows: Array<{ query?: string; page?: string; clicks: number; impressions: number; ctr: number; position: number }> };

function fmtPct(n: number) { return `${(n * 100).toFixed(2)}%`; }
function fmtPos(n: number) { return n ? n.toFixed(1) : "—"; }
function fmtNum(n: number) { return n.toLocaleString(); }
function posClass(p: number) {
 if (!p) return "text-muted-foreground";
 if (p <= 10) return "text-emerald-600 font-medium";
 if (p <= 20) return "text-amber-600";
 return "text-rose-600";
}
function deltaClass(n: number, inverse = false) {
 if (!n) return "text-muted-foreground";
 const good = inverse ? n < 0 : n > 0;
 return good ? "text-emerald-600" : "text-rose-600";
}
function fmtDelta(n: number, kind: "num" | "pct" | "pos") {
 const sign = n > 0 ? "+" : "";
 if (kind === "pct") return `${sign}${(n * 100).toFixed(2)}pp`;
 if (kind === "pos") return `${sign}${n.toFixed(1)}`;
 return `${sign}${fmtNum(Math.round(n))}`;
}

function GscPage() {
 const { user, loading: authLoading, primaryRole } = useAuth();
 const navigate = useNavigate();

 const [sites, setSites] = useState<Array<{ siteUrl: string; permissionLevel: string }>>([]);
 const [siteUrl, setSiteUrl] = useState<string>(DEFAULT_SITE);
 const [days, setDays] = useState<number>(28);
 const [overview, setOverview] = useState<Overview | null>(null);
 const [queries, setQueries] = useState<RowsResp | null>(null);
 const [pages, setPages] = useState<RowsResp | null>(null);
 const [loading, setLoading] = useState(false);
 const [err, setErr] = useState<string | null>(null);
 const [busy, setBusy] = useState<string | null>(null);
 const [sitemapStatus, setSitemapStatus] = useState<Awaited<ReturnType<typeof submitGscSitemap>>["status"] | null>(null);
 const [childSitemaps, setChildSitemaps] = useState<string[]>([]);
 const [selectedChildren, setSelectedChildren] = useState<Set<string>>(new Set());
 const [childStatuses, setChildStatuses] = useState<Record<string, GscSitemapStatus | null>>({});
 const [childErrors, setChildErrors] = useState<Record<string, string>>({});
 const [childFilter, setChildFilter] = useState("");
 const [loadingChildren, setLoadingChildren] = useState(false);

 const fnListSites = useServerFn(listGscSites);
 const fnOverview = useServerFn(getGscOverview);
 const fnQueries = useServerFn(getGscTopQueries);
 const fnPages = useServerFn(getGscTopPages);
 const fnSubmitSitemap = useServerFn(submitGscSitemap);
 const fnSitemapStatus = useServerFn(getGscSitemapStatus);
 const fnClearCache = useServerFn(clearGscCache);
 const fnListChildren = useServerFn(listChildSitemaps);
 const fnSubmitBatch = useServerFn(submitGscSitemapsBatch);
 const fnStatusBatch = useServerFn(getGscSitemapStatusBatch);
 const fnListSubmitted = useServerFn(listSubmittedGscSitemaps);
 const fnDeleteBatch = useServerFn(deleteGscSitemapsBatch);
 const fnComposition = useServerFn(getSitemapComposition);


 const [resetProgress, setResetProgress] = useState<{ done: number; total: number; failed: number } | null>(null);
 const [composition, setComposition] = useState<Awaited<ReturnType<typeof getSitemapComposition>> | null>(null);
 const [loadingComposition, setLoadingComposition] = useState(false);

 useEffect(() => {
 if (authLoading) return;
 if (!user || primaryRole !== "admin") {
 navigate({ to: "/admin-login" });
 return;
 }
 fnListSites()
 .then((r) => {
 setSites(r.sites);
 if (r.sites.length && !r.sites.some((s) => s.siteUrl === siteUrl)) {
 setSiteUrl(r.sites[0].siteUrl);
 }
 })
 .catch((e) => setErr((e as Error).message));
 }, [user, authLoading, primaryRole, navigate, fnListSites]);

 const load = async () => {
 setLoading(true);
 setErr(null);
 try {
 const [o, q, p] = await Promise.all([
 fnOverview({ data: { siteUrl, days, limit: 1 } }),
 fnQueries({ data: { siteUrl, days, limit: 50 } }),
 fnPages({ data: { siteUrl, days, limit: 50 } }),
 ]);
 setOverview(o);
 setQueries(q);
 setPages(p);
 } catch (e) {
 setErr((e as Error).message);
 } finally {
 setLoading(false);
 }
 };

 useEffect(() => {
 if (!siteUrl) return;
 if (authLoading || !user || primaryRole !== "admin") return;
 load();
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [siteUrl, days, authLoading, user, primaryRole]);

 const onRefresh = async () => {
 setBusy("refresh");
 try {
 await fnClearCache();
 await load();
 toast.success("Refreshed from Google");
 } catch (e) {
 toast.error((e as Error).message);
 } finally {
 setBusy(null);
 }
 };

 const sitemapUrl = `${siteUrl.replace(/\/$/, "")}/sitemap.xml`;

 const onSubmitSitemap = async () => {
 setBusy("sitemap");
 try {
 const res = await fnSubmitSitemap({ data: { siteUrl, sitemapUrl } });
 setSitemapStatus(res.status ?? null);
 if (res.status?.state === "accepted") {
 toast.success("Sitemap submitted — Google has read it");
 } else if (res.status?.state === "couldnt_fetch") {
 toast.error("Submitted, but Google couldn't fetch it");
 } else if (res.status?.state === "has_errors") {
 toast.error(`Submitted with ${res.status.errors} error(s)`);
 } else {
 toast.success("Sitemap submitted — waiting for Google to fetch");
 }
 } catch (e) {
 toast.error((e as Error).message);
 } finally {
 setBusy(null);
 }
 };

 const onVerifySitemap = async () => {
 setBusy("verify");
 try {
 const res = await fnSitemapStatus({ data: { siteUrl, sitemapUrl } });
 setSitemapStatus(res.status ?? null);
 if (!res.status) toast.message("No status returned yet");
 } catch (e) {
 toast.error((e as Error).message);
 } finally {
 setBusy(null);
 }
 };

 const loadChildren = async () => {
 setLoadingChildren(true);
 try {
 const r = await fnListChildren({ data: { siteUrl } });
 setChildSitemaps(r.sitemaps);
 } catch (e) {
 toast.error((e as Error).message);
 } finally {
 setLoadingChildren(false);
 }
 };

 const loadComposition = async () => {
 setLoadingComposition(true);
 try {
 const c = await fnComposition();
 setComposition(c);
 } catch (e) {
 toast.error((e as Error).message);
 } finally {
 setLoadingComposition(false);
 }
 };

 useEffect(() => {
 if (!siteUrl || authLoading || !user || primaryRole !== "admin") return;
 loadChildren();
 loadComposition();
 setSelectedChildren(new Set());
 setChildStatuses({});
 setChildErrors({});
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [siteUrl, authLoading, user, primaryRole]);

 const filteredChildren = childSitemaps.filter((s) =>
 childFilter ? s.toLowerCase().includes(childFilter.toLowerCase()) : true,
 );

 const toggleChild = (sm: string) => {
 setSelectedChildren((prev) => {
 const next = new Set(prev);
 if (next.has(sm)) next.delete(sm); else next.add(sm);
 return next;
 });
 };
 const selectAllFiltered = () => setSelectedChildren(new Set(filteredChildren));
 const clearSelection = () => setSelectedChildren(new Set());

 const onSubmitSelectedChildren = async () => {
 const urls = Array.from(selectedChildren);
 if (!urls.length) return;
 setBusy("submit-children");
 try {
 // Batch in chunks of 25 (server caps at 50; keep latency reasonable)
 const chunks: string[][] = [];
 for (let i = 0; i < urls.length; i += 25) chunks.push(urls.slice(i, i + 25));
 let ok = 0, fail = 0;
 for (const chunk of chunks) {
 const res = await fnSubmitBatch({ data: { siteUrl, sitemapUrls: chunk } });
 setChildStatuses((prev) => {
 const next = { ...prev };
 for (const r of res.results) next[r.sitemapUrl] = r.status;
 return next;
 });
 setChildErrors((prev) => {
 const next = { ...prev };
 for (const r of res.results) {
 if (r.ok) delete next[r.sitemapUrl];
 else next[r.sitemapUrl] = r.error || "Submit failed";
 }
 return next;
 });
 ok += res.results.filter((r) => r.ok).length;
 fail += res.results.filter((r) => !r.ok).length;
 }
 if (fail === 0) toast.success(`Submitted ${ok} sitemap(s)`);
 else toast.error(`Submitted ${ok}, failed ${fail}`);
 } catch (e) {
 toast.error((e as Error).message);
 } finally {
 setBusy(null);
 }
 };

 const onVerifySelectedChildren = async () => {
 const urls = Array.from(selectedChildren);
 if (!urls.length) return;
 setBusy("verify-children");
 try {
 const chunks: string[][] = [];
 for (let i = 0; i < urls.length; i += 25) chunks.push(urls.slice(i, i + 25));
 for (const chunk of chunks) {
 const res = await fnStatusBatch({ data: { siteUrl, sitemapUrls: chunk } });
 setChildStatuses((prev) => {
 const next = { ...prev };
 for (const r of res.results) next[r.sitemapUrl] = r.status;
 return next;
 });
 }
 toast.success(`Refreshed ${urls.length} status(es)`);
 } catch (e) {
 toast.error((e as Error).message);
 } finally {
 setBusy(null);
 }
 };

 const onResetGscSitemaps = async () => {
 const rootPath = `${siteUrl.replace(/\/$/, "")}/sitemap.xml`;
 const confirmed = window.confirm(
 `This will delete EVERY sitemap entry from Google Search Console for ${siteUrl} except ${rootPath}.\n\nGoogle will re-discover children from the index over the next 1–2 weeks. Continue?`,
 );
 if (!confirmed) return;
 setBusy("reset-gsc");
 setResetProgress({ done: 0, total: 0, failed: 0 });
 try {
 const list = await fnListSubmitted({ data: { siteUrl } });
 const toDelete = list.sitemaps.filter((p) => p !== rootPath);
 if (!toDelete.length) {
 toast.success("Nothing to delete — only the root sitemap is submitted.");
 setResetProgress(null);
 return;
 }
 setResetProgress({ done: 0, total: toDelete.length, failed: 0 });
 let done = 0, failed = 0;
 for (let i = 0; i < toDelete.length; i += 25) {
 const chunk = toDelete.slice(i, i + 25);
 const res = await fnDeleteBatch({ data: { siteUrl, sitemapUrls: chunk } });
 done += res.results.filter((r) => r.ok).length;
 failed += res.results.filter((r) => !r.ok).length;
 setResetProgress({ done: done + failed, total: toDelete.length, failed });
 }
 if (failed === 0) toast.success(`Deleted ${done} child sitemap(s) from GSC`);
 else toast.error(`Deleted ${done}, failed ${failed}`);
 await loadChildren();
 } catch (e) {
 toast.error((e as Error).message);
 } finally {
 setBusy(null);
 setTimeout(() => setResetProgress(null), 4000);
 }
 };


 if (authLoading) {
 return (
 <div>

 <div className="container mx-auto p-8 flex items-center gap-2">
 <Loader2 className="h-4 w-4 animate-spin" /> Loading…
 </div>
 </div>
 );
 }

  return (
    <AdminPageShell
      title="Google Search Console"
      description="Indexing, sitemap status, and search performance."
      primaryAction={{ label: "Refresh", icon: RefreshCw, onClick: onRefresh }}
    >
      <div className="space-y-4">
        {/* Compact toolbar: site + range */}
        <div className="flex items-center justify-between gap-2 flex-wrap border border-border bg-card px-3 py-2 rounded-md">
          <div className="flex items-center gap-2 flex-wrap">
            <label className="text-[11px] text-muted-foreground uppercase tracking-wide">Site</label>
            <select
              className="h-8 border border-border bg-background px-2 text-sm rounded-md max-w-[280px]"
              value={siteUrl}
              onChange={(e) => setSiteUrl(e.target.value)}
            >
              {sites.length === 0 && <option value={DEFAULT_SITE}>{DEFAULT_SITE}</option>}
              {sites.map((s) => (
                <option key={s.siteUrl} value={s.siteUrl}>{s.siteUrl}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex border border-border rounded-md overflow-hidden">
              {DAY_OPTIONS.map((d) => (
                <button
                  key={d}
                  onClick={() => setDays(d)}
                  className={`px-2.5 h-8 text-xs ${days === d ? "bg-primary text-primary-foreground" : "bg-card hover:bg-muted"}`}
                >
                  {d}d
                </button>
              ))}
            </div>
            <Button size="sm" variant="outline" onClick={onRefresh} disabled={busy === "refresh" || loading}>
              {busy === "refresh" || loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            </Button>
          </div>
        </div>

        {err && (
          <div className="border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700 rounded-md">
            {err}
            {err.includes("403") && (
              <div className="mt-1 text-xs">
                The connected Google account doesn't have access to {siteUrl}.
              </div>
            )}
          </div>
        )}

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { label: "Clicks", value: overview ? fmtNum(overview.totals.clicks) : "—", delta: overview?.deltas.clicks ?? 0, kind: "num" as const, inverse: false },
            { label: "Impressions", value: overview ? fmtNum(overview.totals.impressions) : "—", delta: overview?.deltas.impressions ?? 0, kind: "num" as const, inverse: false },
            { label: "CTR", value: overview ? fmtPct(overview.totals.ctr) : "—", delta: overview?.deltas.ctr ?? 0, kind: "pct" as const, inverse: false },
            { label: "Avg position", value: overview ? fmtPos(overview.totals.position) : "—", delta: overview?.deltas.position ?? 0, kind: "pos" as const, inverse: true },
          ].map((k) => (
            <div key={k.label} className="border border-border bg-card p-3 rounded-md">
              <div className="text-[11px] text-muted-foreground uppercase tracking-wide">{k.label}</div>
              <div className="text-xl font-bold mt-0.5">{k.value}</div>
              {overview && (
                <div className={`text-[11px] mt-0.5 ${deltaClass(k.delta, k.inverse)}`}>
                  {fmtDelta(k.delta, k.kind)} vs prev {days}d
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Performance tables */}
        <div className="grid lg:grid-cols-2 gap-3">
          <div className="border border-border bg-card overflow-hidden rounded-md">
            <div className="px-3 py-2 border-b border-border text-sm font-semibold">Top queries</div>
            <div className="overflow-x-auto max-h-[420px]">
              <table className="w-full text-xs">
                <thead className="bg-muted/40 text-left sticky top-0">
                  <tr>
                    <th className="px-2 py-1.5">Query</th>
                    <th className="px-2 py-1.5 text-right">Clicks</th>
                    <th className="px-2 py-1.5 text-right">Impr.</th>
                    <th className="px-2 py-1.5 text-right">CTR</th>
                    <th className="px-2 py-1.5 text-right">Pos</th>
                  </tr>
                </thead>
                <tbody>
                  {(queries?.rows ?? []).map((r, i) => (
                    <tr key={i} className="border-t border-border">
                      <td className="px-2 py-1 truncate max-w-[220px]" title={r.query}>{r.query}</td>
                      <td className="px-2 py-1 text-right">{fmtNum(r.clicks)}</td>
                      <td className="px-2 py-1 text-right">{fmtNum(r.impressions)}</td>
                      <td className="px-2 py-1 text-right">{fmtPct(r.ctr)}</td>
                      <td className={`px-2 py-1 text-right ${posClass(r.position)}`}>{fmtPos(r.position)}</td>
                    </tr>
                  ))}
                  {!loading && (queries?.rows.length ?? 0) === 0 && (
                    <tr><td colSpan={5} className="p-4 text-center text-muted-foreground">No data yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="border border-border bg-card overflow-hidden rounded-md">
            <div className="px-3 py-2 border-b border-border text-sm font-semibold">Top pages</div>
            <div className="overflow-x-auto max-h-[420px]">
              <table className="w-full text-xs">
                <thead className="bg-muted/40 text-left sticky top-0">
                  <tr>
                    <th className="px-2 py-1.5">Page</th>
                    <th className="px-2 py-1.5 text-right">Clicks</th>
                    <th className="px-2 py-1.5 text-right">Impr.</th>
                    <th className="px-2 py-1.5 text-right">CTR</th>
                    <th className="px-2 py-1.5 text-right">Pos</th>
                  </tr>
                </thead>
                <tbody>
                  {(pages?.rows ?? []).map((r, i) => {
                    const path = r.page ? new URL(r.page).pathname : "";
                    return (
                      <tr key={i} className="border-t border-border">
                        <td className="px-2 py-1 truncate max-w-[220px]">
                          <a href={r.page} target="_blank" rel="noreferrer" className="hover:underline inline-flex items-center gap-1" title={r.page}>
                            <span className="truncate">{path || r.page}</span>
                            <ExternalLink className="h-3 w-3 shrink-0" />
                          </a>
                        </td>
                        <td className="px-2 py-1 text-right">{fmtNum(r.clicks)}</td>
                        <td className="px-2 py-1 text-right">{fmtNum(r.impressions)}</td>
                        <td className="px-2 py-1 text-right">{fmtPct(r.ctr)}</td>
                        <td className={`px-2 py-1 text-right ${posClass(r.position)}`}>{fmtPos(r.position)}</td>
                      </tr>
                    );
                  })}
                  {!loading && (pages?.rows.length ?? 0) === 0 && (
                    <tr><td colSpan={5} className="p-4 text-center text-muted-foreground">No data yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Sitemap management — single consolidated panel */}
        <details className="border border-border bg-card rounded-md group" open>
          <summary className="cursor-pointer list-none px-3 py-2.5 flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-semibold">Sitemap</span>
              {composition && (
                <>
                  <span className="text-[11px] border border-border bg-muted/40 px-1.5 py-0.5 rounded">
                    {composition.totalChildren} children
                  </span>
                  <span className="text-[11px] border border-border bg-muted/40 px-1.5 py-0.5 rounded">
                    ~{composition.totalUrls.toLocaleString()} URLs
                  </span>
                </>
              )}
              {sitemapStatus && (() => {
                const s = sitemapStatus;
                const tone =
                  s.state === "accepted" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                  : s.state === "couldnt_fetch" || s.state === "has_errors" ? "bg-rose-50 text-rose-700 border-rose-200"
                  : "bg-amber-50 text-amber-700 border-amber-200";
                const label =
                  s.state === "accepted" ? "Accepted"
                  : s.state === "couldnt_fetch" ? "Couldn't fetch"
                  : s.state === "has_errors" ? `${s.errors} error(s)`
                  : "Waiting";
                return <span className={`text-[11px] border px-1.5 py-0.5 rounded ${tone}`}>{label}</span>;
              })()}
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
              <a
                href={sitemapUrl}
                target="_blank"
                rel="noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="text-[11px] text-primary hover:underline inline-flex items-center gap-1"
              >
                Open <ExternalLink className="h-3 w-3" />
              </a>
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={(e) => { e.preventDefault(); onSubmitSitemap(); }} disabled={busy === "sitemap"}>
                {busy === "sitemap" ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Send className="h-3 w-3 mr-1" />}
                Submit
              </Button>
              <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={(e) => { e.preventDefault(); onVerifySitemap(); }} disabled={busy === "verify"}>
                {busy === "verify" ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <RefreshCw className="h-3 w-3 mr-1" />}
                Check
              </Button>
            </div>
          </summary>

          <div className="border-t border-border px-3 py-3 space-y-3">
            {/* Composition strip */}
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="text-[11px] text-muted-foreground">
                What <code className="font-mono">/sitemap.xml</code> announces to Google.
                {composition?.providers.lastBuiltAt && (
                  <> Providers built {new Date(composition.providers.lastBuiltAt).toLocaleString()}.</>
                )}
              </div>
              <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={loadComposition} disabled={loadingComposition}>
                {loadingComposition ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
              </Button>
            </div>

            {composition && (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                {composition.fixed.map((f) => (
                  <div key={f.path} className="border border-border bg-background/60 px-2.5 py-1.5 rounded text-xs">
                    <div className="font-medium truncate">{f.label}</div>
                    <code className="text-[10px] text-muted-foreground font-mono truncate block">{f.path}</code>
                    <div className="text-[10px] text-muted-foreground mt-0.5">1 sitemap</div>
                  </div>
                ))}
                <div className="border border-border bg-background/60 px-2.5 py-1.5 rounded text-xs">
                  <div className="font-medium truncate">Providers</div>
                  <code className="text-[10px] text-muted-foreground font-mono truncate block">
                    /sitemap-providers/{`{1..${composition.providers.chunks}}`}.xml
                  </code>
                  <div className="text-[10px] text-muted-foreground mt-0.5">
                    {composition.providers.chunks} · {composition.providers.totalUrls.toLocaleString()} URLs
                  </div>
                </div>
                <div className="border border-border bg-background/60 px-2.5 py-1.5 rounded text-xs">
                  <div className="font-medium truncate">Landing</div>
                  <code className="text-[10px] text-muted-foreground font-mono truncate block">
                    /sitemap-landing/{`{1..${Math.max(1, composition.landing.chunks)}}`}.xml
                  </code>
                  <div className="text-[10px] text-muted-foreground mt-0.5">
                    {composition.landing.chunks} · {composition.landing.totalUrls.toLocaleString()} URLs
                  </div>
                </div>
              </div>
            )}

            {/* Child sitemaps – advanced (collapsed by default) */}
            <details className="border border-border rounded bg-background/40">
              <summary className="cursor-pointer list-none px-2.5 py-2 text-xs font-medium flex items-center justify-between gap-2">
                <span>Child sitemaps (advanced) — {childSitemaps.length} discovered</span>
                <span className="text-[10px] text-muted-foreground">click to expand</span>
              </summary>
              <div className="border-t border-border p-2.5 space-y-2">
                <div className="border border-amber-200 bg-amber-50 px-2.5 py-1.5 rounded text-[11px] text-amber-900 leading-snug">
                  Google discovers children from the index automatically — you don't need to submit them here. "Couldn't fetch" usually means Google hasn't downloaded them yet (not broken). To clean up GSC, use <strong>Reset GSC</strong>.
                </div>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <input
                    value={childFilter}
                    onChange={(e) => setChildFilter(e.target.value)}
                    placeholder="Filter…"
                    className="h-7 w-40 border border-border bg-card px-2 text-xs rounded"
                  />
                  <div className="flex items-center gap-1 flex-wrap">
                    <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={loadChildren} disabled={loadingChildren}>
                      {loadingChildren ? <Loader2 className="h-3 w-3 animate-spin" /> : "Reload"}
                    </Button>
                    <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={selectAllFiltered} disabled={!filteredChildren.length}>All</Button>
                    <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={clearSelection} disabled={!selectedChildren.size}>Clear ({selectedChildren.size})</Button>
                    <Button variant="outline" size="sm" className="h-7 text-xs" onClick={onVerifySelectedChildren} disabled={!selectedChildren.size || busy === "verify-children"}>
                      {busy === "verify-children" ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <RefreshCw className="h-3 w-3 mr-1" />}
                      Check
                    </Button>
                    <Button size="sm" className="h-7 text-xs" onClick={onSubmitSelectedChildren} disabled={!selectedChildren.size || busy === "submit-children"}>
                      {busy === "submit-children" ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Send className="h-3 w-3 mr-1" />}
                      Submit ({selectedChildren.size})
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      className="h-7 text-xs"
                      onClick={onResetGscSitemaps}
                      disabled={busy === "reset-gsc"}
                    >
                      {busy === "reset-gsc" ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Trash2 className="h-3 w-3 mr-1" />}
                      {resetProgress
                        ? `${resetProgress.done}/${resetProgress.total}${resetProgress.failed ? ` (${resetProgress.failed} failed)` : ""}`
                        : "Reset GSC"}
                    </Button>
                  </div>
                </div>

                <div className="max-h-[360px] overflow-y-auto border border-border rounded">
                  {filteredChildren.length === 0 ? (
                    <div className="p-3 text-xs text-muted-foreground">
                      {loadingChildren ? "Loading…" : "No child sitemaps found."}
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 divide-y md:divide-y-0 divide-border">
                      {filteredChildren.map((sm) => {
                        const status = childStatuses[sm];
                        const error = childErrors[sm];
                        const state = status?.state;
                        const tone =
                          error || state === "couldnt_fetch" || state === "has_errors" ? "bg-rose-50 text-rose-700 border-rose-200"
                          : state === "accepted" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                          : state === "pending" ? "bg-amber-50 text-amber-700 border-amber-200"
                          : "bg-muted text-muted-foreground border-border";
                        const label = error ? "Failed"
                          : state === "accepted" ? "OK"
                          : state === "couldnt_fetch" ? "No fetch"
                          : state === "has_errors" ? `${status?.errors ?? 0} err`
                          : state === "pending" ? "Pending"
                          : "—";
                        const path = (() => { try { return new URL(sm).pathname; } catch { return sm; } })();
                        const checked = selectedChildren.has(sm);
                        return (
                          <label key={sm} className="flex items-center gap-2 px-2 py-1.5 text-[11px] hover:bg-muted/40 cursor-pointer border-b md:border-b border-border min-w-0">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleChild(sm)}
                              className="h-3.5 w-3.5 shrink-0"
                            />
                            <span className="flex-1 min-w-0 font-mono truncate" title={sm}>{path}</span>
                            <span className={`border px-1.5 py-0.5 rounded shrink-0 ${tone}`} title={error || ""}>{label}</span>
                            <a
                              href={sm}
                              target="_blank"
                              rel="noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="text-muted-foreground hover:text-foreground shrink-0"
                            >
                              <ExternalLink className="h-3 w-3" />
                            </a>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </details>
          </div>
        </details>

        {overview && (
          <p className="text-[11px] text-muted-foreground">
            Range: {overview.range.startDate} → {overview.range.endDate} · cached 15 min server-side.
          </p>
        )}
      </div>
    </AdminPageShell>
  );
}
